/* eslint-disable no-duplicate-imports */
import {
  ICellRendererParams,
  ITooltipParams,
  RowNode,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from '@ag-grid-community/core';
import { ColDef } from '@ag-grid-community/core';
import partition from 'lodash-es/partition';
import { BehaviorSubject } from 'rxjs';

import { SiteCostType } from '@models/sites/site-cost.enum';

import { AgActionsComponent } from '@shared/ag-components/ag-actions/ag-actions.component';
import { AgCheckboxRendererComponent } from '@shared/ag-components/ag-checkbox-rerender/ag-checkbox-renderer.component';
import { AgRequiredHeaderComponent } from '@shared/ag-components/ag-required-header/ag-required-header.component';
import { TableConstants } from '@shared/constants/table.constants';
import { PatientProtocolSubType, PatientProtocolType } from '@shared/services/gql.service';
import { agNumericOnlyValueSetter } from '@shared/utils/ag-value-setter.utils';
import { Utils } from '@shared/utils/utils';

import { getDiscontinuationColumns } from './protocol-entry-discontinuation-rate-columns';
import { PATIENT_PROTOCOL_VALIDATION_MESSAGE } from '../../../constants/patient-protocol-validation-messages.const';
import { ProtocolEntryGridContext } from '../types/protocol-entry-grid-context';
import { ProtocolEntryGridRow } from '../types/protocol-entry-grid-row';

export function getProtocolEntryColumns(
  cxt: ProtocolEntryGridContext
): ColDef<ProtocolEntryGridRow>[] {
  return [
    ...getPatientProtocolHiddenColumns(),
    ...(cxt.editMode ? [getProtocolEntryActionColumn(cxt)] : []),
    getDescriptionColumn(),
    ...(cxt.selectedCostType === SiteCostType.PATIENT_VISITS ? getPatientVisitsColumns(cxt) : []),
    getPatientProtocolTypeColumn(cxt),
  ];
}

function getDescriptionColumn(): ColDef<ProtocolEntryGridRow> {
  return {
    cellClass: 'text-left',
    cellClassRules: {
      'border-aux-error': (params) => {
        return !!(params.data?.errors?.missingNameError || params.data?.errors?.duplicateNameError);
      },
    },
    field: 'patient_protocol_name',
    headerComponent: AgRequiredHeaderComponent,
    headerName: 'Description',
    minWidth: 150,
    resizable: true,
    tooltipValueGetter: Utils.getDuplicateNameCellTooltip,
    valueSetter: (params: ValueSetterParams) => {
      params.data.patient_protocol_name = params?.newValue?.toString() || '';

      return true;
    },
  };
}

function getPatientProtocolHiddenColumns(): ColDef<ProtocolEntryGridRow>[] {
  return [
    {
      field: 'patient_protocol_id',
      headerName: 'patient_protocol_id',
      hide: true,
    },
    {
      field: 'randomID',
      headerName: 'RANDOM ID',
      hide: true,
    },
    {
      field: 'patient_group_id',
      headerName: 'patient_group_id',
      hide: true,
    },
  ];
}

function getPatientProtocolTypeColumn(cxt: ProtocolEntryGridContext): ColDef<ProtocolEntryGridRow> {
  const getProtocolTypeValues = () => {
    const [visits, others] = partition(cxt.patientProtocolSubTypes, (subType) => {
      return subType.patient_protocol_type.includes('VISIT');
    });

    return cxt.selectedCostType === SiteCostType.PATIENT_VISITS
      ? visits
      : others.filter(
          ({ patient_protocol_type }) =>
            patient_protocol_type !== PatientProtocolType.PATIENT_PROTOCOL_OVERHEAD
        );
  };

  return {
    cellClass: 'text-left',
    cellClassRules: {
      'border-aux-error': (params) => !!params.data?.errors?.missingTypeError,
    },
    cellEditor: 'agRichSelectCellEditor',
    cellEditorParams: () => {
      return {
        values: getProtocolTypeValues(),
      };
    },
    field: 'patient_protocol_sub_type',
    headerComponent: AgRequiredHeaderComponent,
    headerName: 'Type',
    minWidth: 300,
    tooltipValueGetter: (params: ITooltipParams<ProtocolEntryGridRow>) => {
      if (params.data?.errors?.missingTypeError) {
        return PATIENT_PROTOCOL_VALIDATION_MESSAGE.TYPE;
      }

      return params.value?.name;
    },
    valueFormatter: (params: ValueFormatterParams<ProtocolEntryGridRow>) => {
      if (!params.value) {
        return '';
      }

      const subType = params.value as PatientProtocolSubType;

      return subType.name;
    },
    valueGetter: (params: ValueGetterParams<ProtocolEntryGridRow>) => {
      return params.data?.patient_protocol_sub_type as PatientProtocolSubType;
    },
    valueSetter: (params: ValueSetterParams) => {
      const valueFromDropdown = getProtocolTypeValues().find(
        (val) =>
          val.patient_protocol_type === params.newValue ||
          val.name === params.newValue || // when drag event value serialized as label dropdown
          val.name === params.newValue?.name // when select from dropdown or copy paste
      );

      if (params.newValue !== '' && params.newValue !== null) {
        if (!valueFromDropdown) {
          cxt.overlayService.error(PATIENT_PROTOCOL_VALIDATION_MESSAGE.TYPE);

          return false;
        }
      }

      params.data.patient_protocol_sub_type = valueFromDropdown;

      return true;
    },
  };
}

function getPatientVisitsColumns(cxt: ProtocolEntryGridContext): ColDef<ProtocolEntryGridRow>[] {
  return [
    {
      cellClass: [TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
      field: 'target_date_days_out',
      headerName: 'Visit Day',
      minWidth: 150,
      valueFormatter: Utils.preserveZeroDashFormatter,
      valueParser: (params) => {
        return params.newValue === '' ? null : params.newValue;
      },
      valueSetter: (params) =>
        agNumericOnlyValueSetter(
          params,
          cxt.overlayService,
          'Visit Day must be an integer number.'
        ),
    },
    {
      cellClass: [TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
      cellDataType: 'text',
      field: 'target_tolerance_days_out',
      headerName: 'Visit Window [+/-]',
      minWidth: 150,
      valueFormatter: Utils.dashFormatter,
      valueSetter: (params) =>
        agNumericOnlyValueSetter(
          params,
          cxt.overlayService,
          'Visit Window [+/-] must be an integer number.'
        ),
    },
    ...(cxt.flags.investigatorDiscontinuationRate ? getDiscontinuationColumns(cxt) : []),
    {
      cellClass: TableConstants.STYLE_CLASSES.CELL_JUSTIFY_CENTER,
      cellRenderer: AgCheckboxRendererComponent,
      cellRendererParams: (params: ICellRendererParams) => ({
        dontSelectRow: true,
        getDisabledState: () => !cxt.editMode,
        hideLabel: true,
        onChanges: () => {
          cxt.onChanges(params.data, 'optional');
        },
      }),
      colId: 'optional',
      editable: false,
      field: 'optional',
      headerName: 'Optional',
      suppressFillHandle: true,
      suppressSizeToFit: true,
      width: 105,
    },
  ];
}

function getProtocolEntryActionColumn(cxt: ProtocolEntryGridContext): ColDef<ProtocolEntryGridRow> {
  return {
    ...TableConstants.DEFAULT_GRID_OPTIONS.ACTIONS_COL_DEF,
    cellRenderer: AgActionsComponent,
    cellRendererParams: {
      deleteButtonDisabled$: new BehaviorSubject(cxt.deleteDisabled()),
      deleteClickFN: ({ rowNode }: { rowNode: RowNode<ProtocolEntryGridRow> }) => {
        if (rowNode.data) {
          cxt.removeRow(rowNode.data);
        }
      },
    },
    rowDrag: true,
  } as ColDef<ProtocolEntryGridRow>;
}
